import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { GlobeAltIcon, LinkIcon } from '@heroicons/react/solid'

import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import classNames from '@/utils/classNamesLocal'
import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import Pagination from '@/shared/Pagination'
import Card from '@/shared/Card'
import VisibilityControl from '@/shared/VisibilityControl'
import NoResults from '@/pages/Users/ProfileProjects/NoResults'
import NoProjects from '@/pages/Users/ProfileProjects/NoProjects'

// Projects only have two states, and this is the same listing flag the
// manage page uses. Unlisted here means unlisted in search too.
const OPTIONS = [
  { value: 'public', label: 'Listed', icon: GlobeAltIcon },
  { value: 'unlisted', label: 'Unlisted', icon: LinkIcon }
]

const STATUS = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-100',
  open: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-100',
  completed: 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100'
}

const ProjectRow = ({ project }) => {
  const [permissions, setPermissions] = useState(project.permissions)
  const { putpostRequest } = useQuery()

  const change = (perm) => {
    const previous = permissions
    setPermissions(perm)
    putpostRequest(`/api/v3/projects/${project.id}`, 'PATCH', { project: { permissions: perm } }, (err) => { if (err) { setPermissions(previous) } })
  }

  return <li className='flex flex-wrap items-center gap-3 rounded-xl bg-white p-3 shadow-sm dark:bg-gray-700'>
    <img className='h-12 w-12 flex-shrink-0 rounded-lg object-cover' src={project.imageUrl} alt='' />
    <div className='min-w-0 flex-1'>
      <div className='text-xs uppercase text-gray-500 dark:text-gray-300'>{project.createdAt}{ project.deadline && <> to {project.deadline}</> }</div>
      <a href={`/projects/${project.slug}`} className='block truncate text-sm font-semibold text-cccblue'>{project.name}</a>
      <span className={classNames('mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold capitalize', STATUS[project.status] || STATUS.completed)}>{project.status === 'open' ? 'Open' : project.status}</span>
    </div>
    { project.status !== 'draft' && <VisibilityControl value={permissions} onChange={change} options={OPTIONS} /> }
    { project.status === 'draft' && <Link to={`/manage/projects/${project.id}/start`} className='text-xs font-semibold text-cccblue hover:underline'>Finish setting up</Link> }
  </li>
}

const ProfileProjects = () => {
  const [projects, setProjects] = useState([])
  const [meta, setMeta] = useState({})
  const { getRequest } = useQuery()
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [contains, setContains] = useState('')
  const scrollRef = useRef(null)

  const fetchData = (resetPage = false) => {
    setLoading(true)
    getRequest('/api/v3/projects/profile_projects', { contains, page: resetPage ? 1 : page }, (err, jsonData) => {
      setLoading(false)
      if (err) { return }
      setProjects(jsonData.projects)
      setMeta(jsonData.meta)
    })
  }

  useDebouncedEffect(() => { fetchData(true) }, 200, [contains])
  useEffect(() => { fetchData() }, [page])

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Projects on your profile</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Listed projects show on your profile and in search. Unlisted ones are only reachable by link. Same setting as on each project's manage page.</p>
      <p className='mt-3 text-sm text-gray-600 dark:text-gray-300'>Everything else about a project lives in <Link to='/manage/projects'>manage projects</Link>.</p>
    </div>
    <Card>
      <input type='search' value={contains} onChange={(e) => setContains(e.target.value)} placeholder='Search by project name' aria-label='Search projects' />
      <div ref={scrollRef}>
        { loading && <Loading /> }
        { !loading && projects.length === 0 && (contains ? <NoResults /> : <NoProjects />) }
        <ul className='space-y-2'>
          { projects.map((p) => <ProjectRow key={`pro${p.id}`} project={p} />) }
        </ul>
        { projects.length > 0 && <div className='mt-2'><Pagination meta={meta} callback={setPage} scrollRef={scrollRef} /></div> }
      </div>
    </Card>
  </div>
}

export default ProfileProjects

ProjectRow.propTypes = { project: PropTypes.object.isRequired }
